import IconButton from '@mui/material/IconButton'
import clsx from 'clsx'
import moment, { Moment } from 'moment'
import React, { useEffect, useState } from 'react'
import { DATE_FORMAT, MONTH_FORMAT } from '../../constant'
import { convertPriceByCurrency, showAbbrevePrice, getNearestInValidDateV2 } from '../../utils'
// import LoadingSection from './LoadingSection'
import BasicTooltip from '../ui/BasicTooltip'
import { useAppSelector } from '../../redux/hooks'

interface Props {
  months: Moment[]
  // month?: string // format YYYY-MM
  // updateMonth?: (_m: string) => void
  // isShowDoubleMonth?: boolean
  startDate: Moment | null
  endDate: Moment | null
  onDatesChange?: (startDate: Moment | null, endDate: Moment | null) => void
  dataCalendar: any // object
  onNextAndPrev?: (type: 'next' | 'prev', newMonth: Moment) => Promise<void>
  // isLoading?: boolean
  currency?: string
  propertyCurrency?: string
}

const BasicCalendar: React.FC<Props> = ({
  months,
  // month,
  // updateMonth,
  // isShowDoubleMonth,
  onDatesChange,
  startDate,
  endDate,
  dataCalendar,
  onNextAndPrev,
  // isLoading,
  currency = 'USD',
  propertyCurrency = 'USD',
}) => {
  const [currentMonth, setCurrentMonth] = useState<Moment>(moment())
  const [dateFrom, setDateFrom] = useState<Moment | null>(null)
  const [dateTo, setDateTo] = useState<Moment | null>(null)
  const [datesBetweenHoveredAndFrom, setDatesBetweenHoveredAndFrom] = useState<string[]>([])
  const [minimumStay, setMinimumStay] = useState<number>(1)
  const [maximumStay, setMaximumStay] = useState<number>(365)
  const [nearestInValidDate, setNearestInValidDate] = useState<string | null>(null)
  const [dataPrice, setDataPrice] = useState<any>({})
  const { rateObjBaseOnUsd } = useAppSelector((state) => state.property)

  useEffect(() => {
    setDateFrom(startDate)
    setDateTo(endDate)
  }, [startDate, endDate])

  useEffect(() => {
    if (!dateFrom) {
      setDatesBetweenHoveredAndFrom([])
      setNearestInValidDate(null)
    }
  }, [dateFrom])

  useEffect(() => {
    let priceObj: any = {}
    for (let key in dataCalendar) {
      const currencyInCalendar = dataCalendar[key]['currency'] || propertyCurrency
      priceObj[key] = convertPriceByCurrency(rateObjBaseOnUsd, currencyInCalendar, currency, dataCalendar[key]['price'])
    }
    setDataPrice(priceObj)
  }, [dataCalendar, currency, propertyCurrency, rateObjBaseOnUsd])

  const renderDaysOfWeek = () => {
    return moment.weekdaysShort().map((item) => {
      return (
        <div className={'w-[46px] dtravel-text-center'} key={item}>
          {item}
        </div>
      )
    })
  }

  const getDaysByMonth = (_currentMonth: Moment) => {
    const firstDayOfMonth = Number(_currentMonth.startOf('month').format('d')) // 0-> 6
    const numberDaysInMonth = _currentMonth.daysInMonth() // 28 | 30 | 31 days
    const monthFormated = _currentMonth.format(MONTH_FORMAT)

    let blanks = []
    for (let i = 0; i < firstDayOfMonth; i++) {
      blanks.push({})
    }
    let _daysInMonth = []
    for (let d = 1; d <= numberDaysInMonth; d++) {
      const dateFormated = `${monthFormated}-${d > 9 ? d : '0' + d}`
      _daysInMonth.push({
        day: d,
        date: new Date(dateFormated),
        moment: moment(dateFormated, DATE_FORMAT),
        dateFormated: dateFormated,
      })
    }
    let totalSlots = [...blanks, ..._daysInMonth]
    let rows: any[] = []
    let cells: any[] = []
    totalSlots.forEach((row, i) => {
      if (i % 7 !== 0) {
        cells.push(row) // if index not equal 7 that means not go to next week
      } else {
        rows.push(cells) // when reach next week we contain all td in last week to rows
        cells = [] // empty container
        cells.push(row) // in current loop we still push current row to new container
      }
      if (i === totalSlots.length - 1) {
        // when end loop we add remain date
        rows.push(cells)
      }
    })
    return rows
  }

  const handleClickPrevAndNext = async (type: 'next' | 'prev') => {
    const addedMonth = type === 'prev' ? -1 : 1
    const newMonth = moment(currentMonth).add(addedMonth, 'months')

    setCurrentMonth((prevState) => {
      const newMonth = moment(prevState).add(addedMonth, 'months')
      return newMonth
    })
    if (typeof onNextAndPrev === 'function') {
      await onNextAndPrev(type, newMonth)
    }
  }

  const isDateBetweenFromAndTo = (dateFormated: string) => {
    const date = moment(dateFormated, DATE_FORMAT).startOf('days')
    if (dateFrom && dateTo) {
      return dateFrom.isBefore(date) && date.isBefore(dateTo)
    }
    return false
  }

  const handleClickDate = (dateFormated: string) => {
    if ((!dateFrom && !dateTo) || (dateFrom && dateTo)) {
      const dataStartDate = dataCalendar[dateFormated]
      if (dataStartDate) {
        setMinimumStay(dataStartDate.minimumStay)
        setMaximumStay(dataStartDate.maximumStay)
        setNearestInValidDate(getNearestInValidDateV2(moment(dateFormated, DATE_FORMAT), dataCalendar))
      }
    }

    const selectedDate = moment(dateFormated, DATE_FORMAT).startOf('days')
    if ((dateFrom && dateTo) || (!dateFrom && !dateTo)) {
      setDateFrom(selectedDate)
      setDateTo(null)
      if (typeof onDatesChange === 'function') {
        onDatesChange(selectedDate, null)
      }
    }
    if (dateFrom && !dateTo) {
      const isBeforeDateFrom = selectedDate.isBefore(dateFrom)
      if (isBeforeDateFrom) {
        setDateFrom(selectedDate)
        if (typeof onDatesChange === 'function') {
          onDatesChange(selectedDate, null)
        }
      } else {
        setDateTo(selectedDate)
        setDatesBetweenHoveredAndFrom([]) // clear date have red border
        if (typeof onDatesChange === 'function') {
          onDatesChange(dateFrom, selectedDate)
        }
      }
    }
  }

  const handleHoverDate = (dateFormated: string) => {
    const hoveredDate = moment(dateFormated, DATE_FORMAT)
    const isAfterDateFrom = hoveredDate.isAfter(dateFrom)
    if (dateFrom && !dateTo && isAfterDateFrom) {
      const diffDay = hoveredDate.diff(dateFrom, 'days')
      const _list: string[] = []
      for (let i = 1; i < diffDay; i++) {
        _list.push(moment(dateFrom).add(i, 'days').format(DATE_FORMAT))
      }
      setDatesBetweenHoveredAndFrom(_list)
    } else {
      setDatesBetweenHoveredAndFrom([])
    }
  }

  const renderNormalDate = (
    d: any,
    {
      isBetweenFromAndTo,
      isGoingToSelect,
      isPastDate,
      isShowPrice,
      isFromDate,
      isToDate,
      isHasFromDate,
      isHasToDate,
    }: any
  ) => {
    if (!d.dateFormated) {
      return <button className={'w-[46px] dtravel-h-full cursor-auto'} />
    }
    const isSelectedDate = isBetweenFromAndTo || isFromDate || isToDate
    return (
      <button
        className={clsx('w-[46px] dtravel-h-full flex dtravel-flex-col items-center justify-center', {
          'hover:rounded-full': !isSelectedDate,
          'hover:rounded-l hover:rounded-full': isHasFromDate && !isHasToDate && !isFromDate,
          'hover:border hover:border-grayscale-800': !isSelectedDate,
          'hover:!bg-grayscale-200 dtravel-neutral-900': !isSelectedDate,
          '!bg-grayscale-800 text-white hover:bg-grayscale-800': isSelectedDate,
          'border-none bg-grayscale-200 hover:!bg-grayscale-200': isGoingToSelect,
          'rounded-l-full': isFromDate,
          '!rounded-r-full': isToDate,
          'cursor-not-allowed': isHasFromDate && !isHasToDate && isFromDate,
        })}
        data-date={d.dateFormated}
        onClick={() => handleClickDate(d.dateFormated)}
        onMouseEnter={() => handleHoverDate(d.dateFormated)}
        disabled={isPastDate || (isHasFromDate && !isHasToDate && isFromDate)}
      >
        <span className={'dtravel-text-14-20'}>{d.day}</span>
        {isShowPrice && (
          <span
            className={clsx('text-10-12 dtravel-neutral-500', {
              // 'text-red-300': isSelectedDate,
            })}
          >
            {showAbbrevePrice(dataPrice?.[d.dateFormated], currency)}
          </span>
        )}
      </button>
    )
  }

  const renderDayBlocked = (day: string, dateFormated: string) => {
    return (
      <button
        className={clsx(
          'w-[46px] dtravel-h-full flex dtravel-flex-col items-center justify-center dtravel-text-14-18',
          {
            'line-through cursor-not-allowed dtravel-neutral-500': true,
          }
        )}
        onMouseEnter={() => handleHoverDate(dateFormated)}
        data-date={dateFormated}
      >
        <span className={'dtravel-text-14-20'}>{day}</span>
      </button>
    )
  }

  const renderDayInMinMaxNight = ({ title, dateFormated, day, isGoingToSelect }: any) => {
    return (
      <BasicTooltip title={title} placement={'top'} arrow>
        <button
          className={clsx('w-[46px] dtravel-h-full flex dtravel-flex-col items-center justify-center', {
            'opaciy-60 cursor-not-allowed': true,
            'border-none bg-grayscale-200': isGoingToSelect,
            'dtravel-neutral-500': !isGoingToSelect,
            'dtravel-neutral-900': isGoingToSelect,
          })}
          onMouseEnter={() => handleHoverDate(dateFormated)}
          data-date={dateFormated}
        >
          <span className={'dtravel-text-14-20'}>{day}</span>
          <span className={clsx('text-10-12 dtravel-neutral-500')}>
            {showAbbrevePrice(dataPrice?.[dateFormated], currency)}
          </span>
        </button>
      </BasicTooltip>
    )
  }

  const renderDateCheckOutOnly = (day: string, dateFormated: string) => {
    return (
      <BasicTooltip title={'Check-out Only'} placement={'top'} arrow>
        <button
          className={clsx(
            'w-[46px] dtravel-h-full flex dtravel-flex-col items-center justify-center dtravel-rounded-4 dtravel-text-14-18',
            {
              'cursor-not-allowed opacity-60 dtravel-neutral-500': true,
            }
          )}
          onMouseEnter={() => handleHoverDate(dateFormated)}
          data-date={dateFormated}
        >
          <span className={'dtravel-text-14-20'}>{day}</span>
          <span className={clsx('text-10-12 dtravel-neutral-500')}>
            {showAbbrevePrice(dataPrice?.[dateFormated], currency)}
          </span>
        </button>
      </BasicTooltip>
    )
  }

  const renderOneDay = (d: any) => {
    const dataDate = dataCalendar[d.dateFormated]
    const dateInMoment = moment(d.dateFormated, DATE_FORMAT)
    const isPastDate =
      d.dateFormated && moment(d.dateFormated, DATE_FORMAT).startOf('days').isBefore(moment().startOf('days'))
    let isBlocked = true
    if (dataCalendar && Object.prototype.hasOwnProperty.call(dataCalendar, d.dateFormated)) {
      const dateInCalendar = dataCalendar[d.dateFormated]
      isBlocked = !dateInCalendar.bookable && !!dateInCalendar.closedOnCheckout
    }

    if (isPastDate || isBlocked) {
      return renderDayBlocked(d.day, d.dateFormated)
    }

    const isFromDate = d.dateFormated && d.dateFormated === dateFrom?.format(DATE_FORMAT)
    const isToDate = d.dateFormated && d.dateFormated === dateTo?.format(DATE_FORMAT)
    const isBetweenFromAndTo = isDateBetweenFromAndTo(d.dateFormated)
    const isGoingToSelect = datesBetweenHoveredAndFrom.includes(d.dateFormated)

    const isCheckoutOnly = !!(
      dataDate &&
      dataDate.closedOnCheckin &&
      !dataDate.closedOnCheckout &&
      d.dateFormated !== dateTo?.format(DATE_FORMAT)
    )
    const isShowCheckoutOnly = isCheckoutOnly && ((!dateFrom && !dateTo) || (dateFrom && dateTo && !isBetweenFromAndTo))
    // const isShowCheckoutOnly = isCheckoutOnly && !dateFrom && !dateTo

    if (isShowCheckoutOnly) {
      return renderDateCheckOutOnly(d.day, d.dateFormated)
    }
    // minNight, maxNight
    if (dateFrom && !dateTo && dataDate) {
      const isBeforeDateFrom = dateInMoment.startOf('days').isBefore(dateFrom.startOf('days'))
      const isAfterInvalidDate =
        !!nearestInValidDate &&
        dateInMoment.startOf('day').isSameOrAfter(moment(nearestInValidDate, DATE_FORMAT).startOf('day'))
      if (isBeforeDateFrom || isAfterInvalidDate || isBlocked) {
        return renderDayBlocked(d.day, d.dateFormated)
      }
      const diff = dateInMoment.diff(dateFrom, 'day')
      const isLessThanMinNight = 0 < diff && diff < minimumStay
      const isGreaterMaxNight = 0 < diff && maximumStay < diff
      const title = isLessThanMinNight ? `${minimumStay}-night minimum` : `${maximumStay}-night maximum`
      if (isLessThanMinNight || isGreaterMaxNight) {
        return renderDayInMinMaxNight({ day: d.day, dateFormated: d.dateFormated, title, isGoingToSelect })
      }
    }

    const isHasPrice = !!dataDate && dataDate?.price
    return renderNormalDate(d, {
      isBetweenFromAndTo,
      isGoingToSelect,
      isPastDate,
      isFromDate,
      isToDate,
      isHasFromDate: !!dateFrom,
      isHasToDate: !!dateTo,
      isShowPrice: isHasPrice,
    })
  }

  const renderDaysOfMonth = (_currentMonth: Moment, showPrev: boolean, showNext: boolean) => {
    const monthFormated = moment(_currentMonth).format(MONTH_FORMAT)
    return (
      <div>
        <div
          className={
            'dtravel-w-full h-[46px] dtravel-center-horizontal dtravel-flex-between dtravel-inter-400 dtravel-text-14-20 dtravel-neutral-900 px-[6px]'
          }
        >
          <div>
            {showPrev ? (
              <IconButton size={'small'} onClick={() => handleClickPrevAndNext('prev')}>
                <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M11.8054 16.4727C11.545 16.7331 11.1229 16.7331 10.8626 16.4727L6.86258 12.4727C6.73756 12.3477 6.66732 12.1781 6.66732 12.0013C6.66732 11.8245 6.73756 11.6549 6.86258 11.5299L10.8626 7.5299C11.1229 7.26955 11.545 7.26955 11.8054 7.5299C12.0657 7.79025 12.0657 8.21236 11.8054 8.47271L8.94346 11.3346L16.6673 11.3346C17.0355 11.3346 17.334 11.6331 17.334 12.0013C17.334 12.3695 17.0355 12.668 16.6673 12.668L8.94346 12.668L11.8054 15.5299C12.0657 15.7902 12.0657 16.2124 11.8054 16.4727Z"
                    fill="#32302D"
                  />
                </svg>
              </IconButton>
            ) : (
              ''
            )}
          </div>
          <div className={'font-inter-500 text-14-20 text-neutral-900'}>{_currentMonth.format('MMMM YYYY')}</div>
          <div>
            {showNext ? (
              <IconButton size={'small'} onClick={() => handleClickPrevAndNext('next')}>
                <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12.1946 7.52729C12.455 7.26694 12.8771 7.26694 13.1374 7.52729L17.1374 11.5273C17.2624 11.6523 17.3327 11.8219 17.3327 11.9987C17.3327 12.1755 17.2624 12.3451 17.1374 12.4701L13.1374 16.4701C12.8771 16.7305 12.455 16.7305 12.1946 16.4701C11.9343 16.2098 11.9343 15.7876 12.1946 15.5273L15.0565 12.6654L7.33268 12.6654C6.96449 12.6654 6.66601 12.3669 6.66601 11.9987C6.66601 11.6305 6.96449 11.332 7.33268 11.332L15.0565 11.332L12.1946 8.4701C11.9343 8.20975 11.9343 7.78764 12.1946 7.52729Z"
                    fill="#32302D"
                  />
                </svg>
              </IconButton>
            ) : (
              ''
            )}
          </div>
        </div>
        <div
          className={'flex dtravel-dtravel-gap-16 dtravel-inter-500 dtravel-text-14-20 dtravel-neutral-900 mb-[4px]'}
        >
          {renderDaysOfWeek()}
        </div>
        <div className={'flex dtravel-flex-col dtravel-dtravel-gap-16'}>
          {getDaysByMonth(_currentMonth).map((row, index) => {
            if (row.length === 0) return <React.Fragment key={`row_${index}_${monthFormated}`} />
            return (
              <div key={`row_${index}_${monthFormated}`} className={'w-auto h-[46px] flex gap-0 items-center'}>
                {row.map((d: any) => {
                  return <React.Fragment key={d.dateFormated}>{renderOneDay(d)}</React.Fragment>
                })}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (!dataCalendar) {
    return null
  }

  return (
    <div className={'flex dtravel-flex-col dtravel-gap-24'}>
      {Array.isArray(months) &&
        months.map((month: Moment) => {
          // return renderDaysOfMonth(month, false, false)
          return (
            <div id={'dtravelMonth_' + month.format(DATE_FORMAT)} key={month.format(DATE_FORMAT)}>
              {renderDaysOfMonth(month, false, false)}
            </div>
          )
        })}
    </div>
  )
}

export default BasicCalendar
